import { ReactNode, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FeatureId } from '../../../common/iam/access/feature-access/types';
import { ModelEntityWithScores } from '../../../common/model/output/scores/model-entity-data/types';
import { shareImageFullWidth } from '../../../common/renderer/renderer-settings';
import { getShareImageRenderOptions } from '../../../common/renderer/shared';
import { RenderOptions, RenderViewComponentParams, RenderViewComponentType } from '../../../common/renderer/types';
import { BouSpinner } from '../../common/generic/components/progress/spinners/bou-spinner';
import { useHasAccessToFeature, useIsTeam } from '../../common/generic/hooks';
import { BouSpacingLevel, fluidSpacing } from '../../common/generic/layout/bou-spacing';
import { getDefaultRelease, getDefaultVersion } from '../../untidy/redux/selectors';
import { RenderViewCsrdMaterialityMatrix } from './components/render-view-csrd-materiality-matrix';
import { RenderViewHoldingsHistogram } from './components/render-view-holdings-histogram';
import { RenderViewImpactChart } from './components/render-view-impact-chart';
import { RenderViewImpactChartControls } from './components/render-view-impact-chart/controls';
import { RenderViewNetImpactLens } from './components/render-view-lenses/render-view-net-impact-lens';
import { RenderViewNetImpactLensControls } from './components/render-view-lenses/render-view-net-impact-lens/controls';
import { RenderViewSdgAlignments } from './components/render-view-sdg-alignments';
import { RenderViewSdgAlignmentsControls } from './components/render-view-sdg-alignments/controls';
import { RenderViewTopHoldingsTable } from './components/render-view-top-holdings-table';
import { RenderViewValuesAlignmentTable } from './components/render-view-values-alignment-table';
import { RenderViewContextProvider, useRenderViewContextBuilder } from './context';
import { RenderViewControls } from './controls';
import { shouldFetchData } from './utils';
import { getScreenshotAreaStyle } from './utils/get-screenshot-area-style';

interface Props {
  // If given, this hook will use React State to handle changes in options. If not, will use URL instead.
  initialOptions?: Partial<RenderOptions>;

  // TODO OA: Drop this prop and move to load data with ReactQuery, separately for each
  // renderable component.
  data?: ModelEntityWithScores[];
}

export default function RenderView(props: Props) {
  // TODO: remove /profile endpoint specific logic from here as it was moved to RenderApiProfile
  const context = useRenderViewContextBuilder(props.initialOptions, props.data);

  const { data, options, setOptions } = context;
  const items = options.items;

  const hasApiAccess = useHasAccessToFeature(FeatureId.API);
  const hasDownloadAccess = useHasAccessToFeature(FeatureId.DOWNLOAD_IMAGE);
  const isTeam = useIsTeam();
  const release = useSelector(getDefaultRelease);
  const version = useSelector(getDefaultVersion);

  // OA: NOTE: This useEffect hook is only for the purposes of *share images*, i.e. the images
  // that we provide when our entity page url is pasted in FB/LinkedIn/Slack
  // etc. services that automatically fetch an image thumbnail to go along with the link.
  useEffect(() => {
    if (!hasApiAccess && !hasDownloadAccess) {
      // OA: We can't hide everything to keep share images working, so we disable the control UI
      // from those sneaky individuals who shouldn't access the feature but figure out the URL.
      const item = items?.[0];
      if (item && version) {
        const shareImageOptions = getShareImageRenderOptions(item.id, item.type, version, release);
        setOptions(shareImageOptions);
      } else {
        setOptions({ ...options, release, showControlUi: false, isShareImg: true, componentParams: undefined });
      }
    }
    // We don't want to depend on options or setOptions, as those will easily lead to infinite loops
    // TODO: Sort this stuff out in a better way.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDownloadAccess, hasApiAccess, isTeam]);

  if (shouldFetchData(options) && (!data || !items)) {
    return (
      <LoadingRoot>
        <BouSpinner />
      </LoadingRoot>
    );
  }

  const width = options.width || shareImageFullWidth;

  const controlsAndComponents = context.componentParams.map(params => {
    return getControlsAndComponent(params, width);
  });

  const componentControls = controlsAndComponents.map(controlsAndComponent => controlsAndComponent.controls);
  const components = controlsAndComponents.map(controlsAndComponent => controlsAndComponent.component);

  return (
    <RenderViewContextProvider value={context}>
      {options.showControlUi && <RenderViewControls componentControls={componentControls} />}
      <ScreenshotArea options={{ ...options, width }}>{components}</ScreenshotArea>
    </RenderViewContextProvider>
  );
}

const LoadingRoot = styled.div`
  ${fluidSpacing('margin-top', BouSpacingLevel.SMALL)}
  display: flex;
  justify-content: center;
`;

type ScreenshotAreaProps = Partial<RenderOptions>;

const ScreenshotArea = styled.div<{ options: ScreenshotAreaProps }>`
  ${props => getScreenshotAreaStyle(props.options)}
`;

type ControlsAndComponent = { controls: ReactNode | undefined; component: ReactNode };

const getControlsAndComponent = (params: RenderViewComponentParams, width: number): ControlsAndComponent => {
  const key = params.componentType;
  switch (params.componentType) {
    case RenderViewComponentType.IMPACT_CHART:
      return {
        controls: <RenderViewImpactChartControls {...{ params, key }} />,
        component: <RenderViewImpactChart {...{ width, params, key }} />,
      };
    case RenderViewComponentType.VALUES_ALIGNMENT:
      return { controls: undefined, component: <RenderViewValuesAlignmentTable {...{ key }} /> };
    case RenderViewComponentType.TOP_HOLDINGS:
      return { controls: undefined, component: <RenderViewTopHoldingsTable {...{ key }} /> };
    case RenderViewComponentType.HOLDINGS_HISTOGRAM:
      return { controls: undefined, component: <RenderViewHoldingsHistogram {...{ key }} /> };
    case RenderViewComponentType.LENS_NET_IMPACT:
      return {
        controls: <RenderViewNetImpactLensControls {...{ params, key }} />,
        component: <RenderViewNetImpactLens {...{ params, key }} />,
      };
    case RenderViewComponentType.SDG_ALIGNMENTS:
      return {
        controls: <RenderViewSdgAlignmentsControls {...{ params, key }} />,
        component: <RenderViewSdgAlignments {...{ params, key }} />,
      };
    case RenderViewComponentType.CSRD_MATERIALITY_MATRIX:
      return {
        controls: undefined,
        component: <RenderViewCsrdMaterialityMatrix {...{ params, key }} />,
      };
    default:
      return { controls: undefined, component: <div>NOT IMPLEMENTED YET</div> };
  }
};
